"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/common/helpers/utils";

function RadioGroup({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return <RadioGroupPrimitive.Root data-slot="radio-group" className={cn("grid gap-3", className)} {...props} />;
}

function RadioGroupItem({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        "border-muted-foreground/50 text-primary data-[state=checked]:border-primary aspect-square bg-transparent",
        "size-4 shrink-0 rounded-full border shadow-xs transition-colors disabled:cursor-not-allowed dark:shadow-none",
        "disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

interface RadioGroupCardItemProps extends React.ComponentProps<typeof RadioGroupPrimitive.Item> {
  description?: React.ReactNode;
  label: React.ReactNode;
  selected?: boolean;
}

function RadioGroupCardItem({
  className,
  description,
  id,
  label,
  selected = false,
  ...props
}: RadioGroupCardItemProps) {
  const generatedId = React.useId();
  const itemId = id ?? generatedId;

  return (
    <label
      htmlFor={itemId}
      className={cn(
        "bg-input-background/40 hover:bg-input-background/70 flex cursor-pointer gap-3 rounded-lg border p-4 text-left transition-colors",
        "focus-within:ring-ring focus-within:ring-1 focus-within:outline-none",
        "has-disabled:cursor-not-allowed has-disabled:opacity-50",
        selected && "border-primary bg-primary/10",
        className,
      )}
    >
      <RadioGroupItem id={itemId} className="mt-0.5" {...props} />
      <span className="grid gap-1">
        <span className="text-sm font-medium">{label}</span>
        {description ? <span className="text-muted-foreground text-sm leading-relaxed">{description}</span> : null}
      </span>
    </label>
  );
}

export { RadioGroup, RadioGroupCardItem, RadioGroupItem };
